import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { pingAPI } from "@/lib/ping-api";
import { queryKeys } from "@/lib/query-keys";
import type { ChatListParams } from "@/types";
import { useAuthStore } from "@/stores/authStore";

// Hook for fetching a paginated list of chats (now with real-time updates instead of polling)
export const usePingChats = (
  params?: ChatListParams,
  enabled: boolean = true
) => {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: queryKeys.ping.chats.list(params),
    queryFn: () => pingAPI.chats.list(params),
    retry: false,
    staleTime: Infinity, // Never go stale - rely on real-time updates for freshness
    gcTime: 300000, // 5 minutes
    enabled: !!user && enabled,
  });
};

export const usePingChatsInfinite = (
  params?: ChatListParams,
  enabled: boolean = true
) => {
  const { user } = useAuthStore();
  return useInfiniteQuery({
    queryKey: queryKeys.ping.chats.list(params),
    queryFn: ({ pageParam }) =>
      pingAPI.chats.list({ ...params, page: pageParam }),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.next ? pages.length + 1 : undefined,
    retry: false,
    staleTime: Infinity,
    gcTime: 300000,
    enabled: !!user && enabled,
  });
};

// Hook for fetching eligible reassignment targets for a specific chat
export const useReassignmentTargets = (
  chatId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: queryKeys.ping.chats.reassignmentTargets(chatId),
    queryFn: () => pingAPI.chats.getReassignmentTargets(chatId),
    enabled: !!chatId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Hook for fetching a single chat detail
export const usePingChatDetail = (chatId: string, enabled: boolean = true) => {
  const query = useQuery({
    queryKey: queryKeys.ping.chats.detail(chatId),
    queryFn: () => {
      return pingAPI.chats.get(chatId);
    },
    select: (data) => {
      // The apiRequest wrapper already unwraps the data, so we don't need data.data
      return data;
    },
    enabled: !!chatId && enabled,
    retry: false,
    staleTime: Infinity, // Never go stale - rely on real-time updates
  });
  return query;
};
